/**
 * PATH: packages/kanban-host/src/PlannerPage.tsx
 * FILE_ID: KANBANHOST-PLANNERPAGE-001
 * PURPOSE: Canonical body of this kanban-allocation module.
 *          Lifted from apps/<tenant>/src/features/kanban/PlannerPage.tsx
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md K4).
 * WHY:     Removes the previous tenant-side duplication of
 *          this byte-identical body. One source of truth, both
 *          tenants consume from this package via thin re-export
 *          shims at apps/<tenant>/src/features/kanban/<rel>.
 * RELATED:
 *   - apps/<tenant>/src/features/kanban/PlannerPage.tsx: re-export shim
 *   - packages/email-hub/src/_stubs/kanban/*: email-hub side
 *     mirrors that depend on these symbols.
 */

import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, CalendarClock, CalendarDays, GanttChartSquare } from 'lucide-react'
import {
  BoardGanttTimelineView,
  CardDetailDrawer,
  KanbanEmptyState,
  KanbanErrorState,
  KanbanModeTabs,
  KanbanSkeleton,
  RemindersList,
  type Board,
  type Card,
  type Lane,
} from '@quotech/ui/kanban'

import { useApiAuth } from './auth/useApiAuth'
import { useToast } from './notify/useToast'
import { useUiStore } from '@quotech/tenant-shared/stores/uiStore'
import {
  getQuotechOrgIdOverride,
  getRuntimeQuotechOrgId,
} from '@quotech/tenant-shared/services/quotechApi'
import { getSamKanbanApi } from './api'
import { resolveKanbanOrgId } from './kanbanOrgIdResolver'

/** Build an in-memory Lane object for the planner timeline. The
 *  planner doesn't persist its lanes server-side; they're synthetic
 *  groupings of cards from real boards. */
function buildSyntheticLane(id: string, name: string, color: string): Lane {
  const now = new Date().toISOString()
  return {
    id,
    board: 'planner',
    name,
    kind: 'CUSTOM',
    position: 0,
    description: '',
    color,
    wip_limit: 0,
    action_template_ids_json: [],
    classification_prompt_override: '',
    ai_suggested_at: null,
    ai_suggestion_reason: '',
    approved_at: null,
    archived: false,
    triggers: [],
    created_at: now,
    updated_at: now,
  }
}

// Planner is a reference projection, so it loads the document tool only after
// an operator explicitly opens a source card's Workbook action.
const KanbanCardWorkbookModal = lazy(async () => {
  const module = await import('@quotech/ui/kanban/workbook')
  return { default: module.KanbanCardWorkbookModal }
})

export default function PlannerPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { session } = useApiAuth()
  const { toast } = useToast()
  const api = useMemo(() => getSamKanbanApi(), [])
  const queryClient = useQueryClient()
  const [activeCard, setActiveCard] = useState<Card | null>(null)
  const [workbookCard, setWorkbookCard] = useState<Card | null>(null)
  // FEAT-2026-06-03-KANBAN-DUE-SOON-FEED — Timeline (the existing
  // assignment-scoped Gantt) vs Reminders (a flat, cross-board "what's due,
  // soonest first" agenda fed by GET /cards/due-soon/). Kept as a local
  // view-toggle on the Planner rather than a new global mode tab so we don't
  // crowd the workspace chrome (operator: "I'd rather a little inconvenience
  // than overcrowd").
  const [view, setView] = useState<'timeline' | 'reminders'>('timeline')
  const setShellSidebarCollapsed = useUiStore(s => s.setSidebarCollapsed)
  // BUG-2026-05-14-KANBAN-ORG-ID-MISMATCH (REC-2026-05-14, app #8 of the
  // ``id-identity-at-every-boundary`` principle). PlannerPage talks to
  // the master Django backend at ``quotech-backend.quotech.ai/api/kanban/``,
  // which keys RBAC + uniqueness on the MASTER Quotech ``Organisation.id``.
  // The session's ``organisation.id`` is the LOCAL sam-api primary key
  // (Newmark = 20 locally / 6 on master). Resolver priority chain +
  // rationale: see ``./kanbanOrgIdResolver.ts``. Sister fix to KanbanPage
  // landed in qf!2930 — that one shipped without touching this file, so
  // the same bug stayed live on the Planner route. Closing the gap here.
  const orgId = resolveKanbanOrgId(session, {
    runtimeId: getRuntimeQuotechOrgId(),
    buildtimeId: getQuotechOrgIdOverride(),
  })
  const userId = session?.user?.id ?? null

  // Mirror KanbanPage's auto-collapse so the planner gets the full
  // viewport — the timeline needs every horizontal pixel.
  useEffect(() => {
    const prev = useUiStore.getState().sidebarCollapsed
    if (!prev) setShellSidebarCollapsed(true)
    return () => {
      setShellSidebarCollapsed(prev)
    }
  }, [setShellSidebarCollapsed])

  // Phase 1 task-allocation: prefer the server-side
  // ``assigned-to-me`` feed (only MY cards across all boards,
  // RBAC/tenant-fenced) over fetching every visible card and
  // filtering client-side. ``includeDone`` keeps the timeline's prior
  // "all my cards, any status" semantics so completed bars still
  // render. Falls back to the legacy fetch-all + client-side
  // swimlane_user/assigned_user_ids filter when the api impl predates
  // the endpoint.
  const useAssignedFeed = typeof api.assignedToMe === 'function'
  const cardsQ = useQuery({
    queryKey: ['planner', 'cards', orgId, userId, useAssignedFeed],
    queryFn: () =>
      useAssignedFeed
        // FEAT-2026-06-30-KANBAN-PLANNER-INBOX — `includeInbox` widens the
        // feed to cards the operator OWNS via their swimlane (inbox), not
        // just ones formally "assigned" to them. Without it an inbox-driven
        // operator saw an almost-empty Planner ("why do I see only one?").
        ? api.assignedToMe!({
            organisationId: orgId,
            includeDone: true,
            includeInbox: true,
          })
        : api.listCards({}),
    enabled: Boolean(orgId),
    staleTime: 30 * 1000,
  })

  const boardsQ = useQuery({
    queryKey: ['planner', 'boards', orgId],
    queryFn: () =>
      api.listBoards(orgId != null ? { organisationId: orgId } : {}),
    enabled: Boolean(orgId),
    staleTime: 5 * 60 * 1000,
  })

  // Reminders feed — every card the operator can see that carries a
  // due_date, across ALL boards (not assignment-scoped like the timeline).
  // Falls back to a client-side filter if the api impl predates the
  // dedicated endpoint. Only fetched when the Reminders view is open.
  const remindersQ = useQuery({
    queryKey: ['planner', 'due-soon', orgId],
    queryFn: () =>
      api.listCardsDueSoon
        ? api.listCardsDueSoon({ organisationId: orgId, horizonDays: 0 })
        : api.listCards({}).then((cs) => cs.filter((c) => c.due_date)),
    enabled: Boolean(orgId) && view === 'reminders',
    staleTime: 30 * 1000,
  })

  // Per-board lane lookup. Fetched lazily after boards land. We
  // need this because Card carries `lane` (UUID) but not its
  // parent board, so we have to walk every board's lane list to
  // build a `lane → board` index. Cached for 5min so it's cheap
  // on revisits.
  const lanesByBoardQ = useQuery({
    queryKey: ['planner', 'lanes-by-board', orgId, (boardsQ.data ?? []).map((b) => b.id)],
    queryFn: async () => {
      const all = boardsQ.data ?? []
      const lanesPerBoard = await Promise.all(
        all.map(async (b) => ({ board: b, lanes: await api.listLanes(b.id) })),
      )
      const laneToBoard = new Map<string, string>()
      for (const { board: b, lanes: ls } of lanesPerBoard) {
        for (const l of ls) laneToBoard.set(l.id, b.id)
      }
      return {
        laneToBoard,
        sourceLanes: lanesPerBoard.flatMap(({ lanes }) => lanes),
      }
    },
    enabled: Boolean(orgId) && (boardsQ.data?.length ?? 0) > 0,
    staleTime: 5 * 60 * 1000,
  })

  // Build synthetic "lanes" — one row per source board. Cards
  // assigned to the user that belong to that board appear on its
  // row. This makes the timeline read as "Job A: …bars… / Job B:
  // …bars… / etc." which is what the operator wants. Cards we
  // can't trace back to a board land in an "Other" bucket.
  const { lanes, cards, displayLaneIdByCardId } = useMemo(() => {
    const allCards = cardsQ.data ?? []
    const allBoards = boardsQ.data ?? []
    const laneToBoard = lanesByBoardQ.data?.laneToBoard ?? new Map<string, string>()

    // Server feed is already scoped to me; the legacy path still needs
    // the client-side narrowing by swimlane_user / assigned_user_ids.
    const mine = useAssignedFeed
      ? allCards
      : userId
        ? allCards.filter((c) => c.swimlane_user === userId || (c.assigned_user_ids ?? []).includes(userId))
        : allCards.filter((c) => c.status === 'OPEN' || c.status === 'BLOCKED')

    // Group cards by their inferred board.
    const cardsByBoard: Record<string, Card[]> = {}
    for (const c of mine) {
      const boardId = laneToBoard.get(c.lane) ?? 'planner:other'
      ;(cardsByBoard[boardId] ??= []).push(c)
    }

    // Build a synthetic lane per board that has at least one
    // assigned card. Stable ordering: pinned > by name. The
    // "Other" bucket sits at the end so unmapped cards aren't
    // hidden but don't crowd the top.
    const palette = [
      '#0EA5E9', '#10B981', '#6366F1', '#F59E0B', '#EC4899',
      '#14B8A6', '#A855F7', '#F43F5E', '#84CC16', '#0891B2',
    ]
    const out: Lane[] = []
    const remapped: Card[] = []
    const displayLaneIds = new Map<string, string>()

    // Stable name order. (A `pinned` comparison used to sit here but
    // `Board` has no such wire field — pins are per-user rows fetched
    // via `listBoardPins` — so it compared `undefined !== undefined`
    // and never reordered anything. Removed rather than faked.)
    const sortedBoards = [...allBoards].sort((a, b) =>
      a.name.localeCompare(b.name),
    )

    for (const b of sortedBoards) {
      const list = cardsByBoard[b.id]
      if (!list?.length) continue
      const synthId = `planner:${b.id}`
      out.push(buildSyntheticLane(synthId, b.name, palette[out.length % palette.length]))
      for (const c of list) {
        // Display identity is synthetic; mutation identity stays the original
        // real lane id on the Card so drawer lane edits never target planner:*.
        remapped.push(c)
        displayLaneIds.set(c.id, synthId)
      }
    }
    if (cardsByBoard['planner:other']?.length) {
      const synthId = 'planner:other'
      out.push(buildSyntheticLane(synthId, 'Other', '#94A3B8'))
      for (const c of cardsByBoard[synthId] ?? []) {
        remapped.push(c)
        displayLaneIds.set(c.id, synthId)
      }
    }

    // Fallback: if we have cards but no board yet (e.g. lanes are
    // still loading), keep the original single-lane behaviour so
    // the timeline doesn't blank out.
    if (out.length === 0 && mine.length > 0) {
      const synthId = 'planner:my-work'
      out.push(buildSyntheticLane(synthId, 'My work (all boards)', '#6366F1'))
      for (const c of mine) {
        remapped.push(c)
        displayLaneIds.set(c.id, synthId)
      }
    }

    return { lanes: out, cards: remapped, displayLaneIdByCardId: displayLaneIds }
  }, [cardsQ.data, boardsQ.data, lanesByBoardQ.data, userId, useAssignedFeed])

  // Synthetic Board so BoardGanttTimelineView's `void board`
  // signature is happy. Using a JOB kind so the timeline inherits
  // the auto-theme palette via the BoardView wrapper (when we wire
  // it; the standalone planner doesn't theme yet but will in a
  // follow-up).
  const syntheticBoard: Board = useMemo(
    () => ({
      id: 'planner',
      organisation: (orgId as number) ?? 0,
      name: 'Planner',
      slug: 'planner',
      kind: 'JOB',
      visibility: 'ORG',
      description: '',
      owner: null,
      job: null,
      lanes: [],
      memberships: [],
      created_by: null,
      archived: false,
      display_order: 0,
      config_json: {},
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }),
    [orgId],
  )

  return (
    // qf!2510 — match KanbanPage: stop hard-coding the shell
    // header height. The shell renders Header + (optional)
    // banners + TabBar above `<main flex-1 min-h-0
    // overflow-hidden>`, so `h-full` lets flex compute the
    // actual available space and keeps the bottom toolbar in
    // view regardless of which chrome rows are visible.
    <div
      className="flex h-full min-h-0 flex-col transition-[padding] duration-200"
      style={{
        // Mirror KanbanBoardView: shrink content when CardDetailDrawer
        // is pinned as a split pane (CSS var published by the drawer).
        paddingRight: 'var(--quotech-kanban-pinned-rail-w, 0px)',
      }}
    >
      <header className="flex flex-shrink-0 items-center gap-3 border-b border-border bg-background px-4 py-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Back"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <h1 className="text-base font-semibold">Planner</h1>
          <span
            className="text-xs text-muted-foreground"
            title={
              view === 'timeline'
                ? 'Cards assigned to you OR in your inbox, across every board. Excludes done & archived.'
                : 'Cards with a due date across every board, soonest first.'
            }
          >
            {view === 'timeline'
              ? `${cards.length} card${cards.length === 1 ? '' : 's'} for you · all boards`
              : `${(remindersQ.data ?? []).length} due`}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-0.5 rounded-lg border border-border p-0.5">
          <button
            type="button"
            onClick={() => setView('timeline')}
            className={
              'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ' +
              (view === 'timeline'
                ? 'bg-accent text-foreground'
                : 'text-muted-foreground hover:text-foreground')
            }
          >
            <GanttChartSquare className="h-3.5 w-3.5" /> Timeline
          </button>
          <button
            type="button"
            onClick={() => setView('reminders')}
            className={
              'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ' +
              (view === 'reminders'
                ? 'bg-accent text-foreground'
                : 'text-muted-foreground hover:text-foreground')
            }
          >
            <CalendarClock className="h-3.5 w-3.5" /> Reminders
          </button>
        </div>
      </header>
      <main className="min-h-0 flex-1 overflow-hidden bg-background">
        {view === 'reminders' ? (
          remindersQ.isLoading ? (
            <KanbanSkeleton laneCount={1} cardCount={6} />
          ) : remindersQ.isError ? (
            <div className="flex h-full items-center justify-center">
              <KanbanErrorState
                title="Couldn't load reminders"
                message={(remindersQ.error as Error)?.message ?? undefined}
                onRetry={() => void remindersQ.refetch()}
              />
            </div>
          ) : (
            <div className="h-full overflow-y-auto">
              <RemindersList
                cards={remindersQ.data ?? []}
                onOpen={(c) => setActiveCard(c)}
              />
            </div>
          )
        ) : cardsQ.isLoading ? (
          <KanbanSkeleton laneCount={1} cardCount={5} />
        ) : cardsQ.isError ? (
          <div className="flex h-full items-center justify-center">
            <KanbanErrorState
              title="Couldn't load planner"
              message={(cardsQ.error as Error)?.message ?? undefined}
              onRetry={() => void cardsQ.refetch()}
            />
          </div>
        ) : cards.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <KanbanEmptyState
              icon={CalendarDays}
              title="Nothing scheduled for you"
              body="The planner shows every card assigned to you — or sitting in your inbox — across every board on a unified Gantt timeline. Assign yourself to a card (or set its dates) on any board to see it here."
              cta={{ label: 'Open a board', onClick: () => navigate('/kanban') }}
            />
          </div>
        ) : (
          <BoardGanttTimelineView
            board={syntheticBoard}
            cards={cards}
            lanes={lanes}
            // FEAT-2026-06-30-KANBAN-PLANNER-TEAM-TOGGLE — the Planner is a
            // personal, "my cards" feed, so the Gantt's "Team" (group-by-
            // assignee) view would only ever show the current user — a
            // misleading control here. Hide it; rows stay grouped by board.
            enableAssigneeGrouping={false}
            onCardOpen={(c) => setActiveCard(c)}
            getDisplayLaneId={(card) =>
              displayLaneIdByCardId.get(card.id) ?? card.lane
            }
            cardSurfaceId="planner-timeline-bar"
            onCardReschedule={async (card, patch) => {
              try {
                await api.updateCard(card.id, patch as never)
                await cardsQ.refetch()
                toast('Rescheduled', 'success')
              } catch (err) {
                toast(
                  err instanceof Error ? err.message : 'Reschedule failed',
                  'error',
                )
              }
            }}
          />
        )}
      </main>
      <CardDetailDrawer
        api={api}
        card={activeCard}
        // Synthetic per-board lanes — gives the lane label something to
        // resolve against. (Previously omitted entirely, which crashed
        // `lanes.find` the moment a card opened on this page; `notify`
        // was also passed but has never been a drawer prop.)
        lanes={lanesByBoardQ.data?.sourceLanes ?? []}
        open={Boolean(activeCard)}
        onClose={() => setActiveCard(null)}
        // The Planner's synthetic rows never cross this boundary. The drawer
        // receives the original Card, and the document API is card-scoped.
        onOpenWorkbook={
          api.getCardWorkbook && api.saveCardWorkbook
            ? (card) => setWorkbookCard(card)
            : undefined
        }
      />
      {workbookCard && api.getCardWorkbook && api.saveCardWorkbook && (
        <Suspense fallback={null}>
          <KanbanCardWorkbookModal
            key={workbookCard.id}
            card={workbookCard}
            getWorkbook={api.getCardWorkbook}
            saveWorkbook={api.saveCardWorkbook}
            notify={(kind, message) => toast(message, kind)}
            onSaved={() => {
              const sourceBoardId = lanesByBoardQ.data?.laneToBoard.get(
                workbookCard.lane,
              )
              if (sourceBoardId) {
                void queryClient.invalidateQueries({
                  queryKey: ['kanban', 'cards', sourceBoardId],
                })
              }
              void queryClient.invalidateQueries({
                queryKey: ['planner', 'cards'],
              })
              void queryClient.invalidateQueries({
                queryKey: ['planner', 'due-soon'],
              })
            }}
            onClose={() => setWorkbookCard(null)}
          />
        </Suspense>
      )}
      {/* FEAT-2026-05-14-KANBAN-TRELLO-PARITY: bottom-floating mode
       *  switcher. Mounted on every /kanban/* surface so the operator
       *  can flip between Inbox / Planner / Board / Switch boards
       *  without walking the chrome sidebar. ``activeMode`` is forced
       *  to "planner" so the tab stays lit even if the route is
       *  ambiguous (e.g. a deep-link with extra segments).  */}
      <KanbanModeTabs
        currentPath={location.pathname}
        activeMode="planner"
        onNavigate={(href) => navigate(href)}
      />
    </div>
  )
}
